import type { CSSProperties, ReactNode } from 'react';
import {
  childTicketToneVar,
  type ChildTicketTone,
} from '@cortex/domain-product-innovation-lab-basic-design-tree';

/**
 * トーンから、ラベル 1 枚ぶんのベタ塗り色（塗り + その上に載る文字）を組む。
 * 半透明の tint にしないのは、ダークの黒地では tint が沈んで種別どうしの
 * 区別が付かなくなるため。色は必ず CSS 変数経由で引く。
 * @graph-connects childTicketToneVar [calls] トーン → CSS 変数名
 */
export function childTicketToneStyle(tone: ChildTicketTone): CSSProperties {
  const style: CSSProperties & { '--child-ticket-glow': string } = {
    background: `var(${childTicketToneVar(tone)})`,
    color: `var(${childTicketToneVar(tone)}-on)`,
    // 選択中の札をどの色で光らせるかは CSS 側（.child-kind-option 等）が決めるので、
    // 塗りの色をそのまま変数で渡しておく。輪郭を足さずに明るさだけで選択を示すため。
    '--child-ticket-glow': `var(${childTicketToneVar(tone)})`,
  };
  return style;
}

/**
 * @graph-stack product-innovation-lab
 * @graph-domain UX
 * @graph-business 子票種別を 1 枚のベタ塗りラベルで見せる。群色（開発=緑 / 結合テスト=青 / 総合テスト・リリース=紫）に加え、Buffer 系は黒灰、Release 系はピンクで塗り分ける
 * @graph-connects childTicketToneStyle [calls] トーンごとのベタ塗り色
 */
export function ChildTicketKindLabel({
  label,
  tone,
  size = 'md',
}: {
  /** 表示する種別名。 */
  label: string;
  /** 色トーン。 */
  tone: ChildTicketTone;
  /** 大きさ。`sm` はメニューの候補、`md` は表の中の選択済みラベル。 */
  size?: 'sm' | 'md';
}): ReactNode {
  return (
    <span
      className={`inline-flex max-w-full items-center rounded-full font-semibold whitespace-nowrap ${
        size === 'sm' ? 'px-3 py-1 text-[11px]' : 'px-3 py-1.5 text-xs'
      }`}
      style={childTicketToneStyle(tone)}
    >
      <span className="truncate">{label}</span>
    </span>
  );
}
